import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, PlusIcon, UserIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  PropertyManager,
  useCreatePropertyManager,
  useUpdatePropertyManager,
} from '@/api/property-managers'
import { AvatarFormField, PasswordInputFormField, PhoneNumberFormField } from '@/components/form'
import { ChipSelectFormField } from '@/components/form/chip-select-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { ChipSelectOption } from '@/components/ui/chip-select'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { Modal } from '@/components/ui/modal'
import { EMPTY_VALUE } from '@/constants/ui'
import { toastFactory } from '@/factories'
import { useDirtyFormGuard } from '@/hooks'
import { selectBuildings } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils/error'
import { formatPhoneNumberDisplay } from '@/utils/phone-number'

import { FormChangeFields, getFormChanges, PASSWORD_MASK } from '../../shared/form-changes'
import { getPersonAvatarSrc } from '../../shared/person-avatar'
import { PersonFormLayout } from '../../shared/person-form-layout'
import { getPhoneDigits } from '../../shared/person-table.utils'
import {
  makePropertyManagerFormSchema,
  PROPERTY_MANAGER_JOB_TITLE_MAX_LENGTH,
  PROPERTY_MANAGER_NAME_MAX_LENGTH,
  PropertyManagerFormData,
  PropertyManagerFormMode,
} from './property-manager-form-modal.constants'

interface PropertyManagerFormModalProps {
  mode: PropertyManagerFormMode
  onClose: VoidFunction
  propertyManager?: PropertyManager
}

export const PropertyManagerFormModal = ({
  mode,
  onClose,
  propertyManager,
}: PropertyManagerFormModalProps) => {
  const { t } = useTranslation(['organisation', 'common'])
  const buildings = useSelector(selectBuildings)

  const { mutateAsync: createPropertyManager } = useCreatePropertyManager()
  const { mutateAsync: updatePropertyManager } = useUpdatePropertyManager()

  const isEdit = mode === 'edit'

  const schema = useMemo(() => makePropertyManagerFormSchema(t, mode), [t, mode])

  const buildingOptions = useMemo<ChipSelectOption[]>(
    () =>
      buildings.map((building) => ({
        label: building.name ?? String(building.id),
        value: String(building.id),
      })),
    [buildings],
  )

  const existingAvatarUrl =
    isEdit && propertyManager ? getPersonAvatarSrc(propertyManager.user.avatar) : undefined

  const form = useForm<PropertyManagerFormData>({
    defaultValues: {
      avatar: [],
      buildingIds: propertyManager?.buildingIds.map((building) => String(building.id)) ?? [],
      email: propertyManager?.user.email ?? '',
      firstName: propertyManager?.user.firstName ?? '',
      jobTitle: propertyManager?.jobTitle ?? '',
      lastName: propertyManager?.user.lastName ?? '',
      password: '',
      phone: formatPhoneNumberDisplay(propertyManager?.phone),
    },
    mode: 'onTouched',
    resolver: zodResolver(schema),
  })

  const { isDirty, isSubmitting } = form.formState

  const changeFields = useMemo<FormChangeFields<PropertyManagerFormData>>(() => {
    const buildingNames = new Map(buildingOptions.map((option) => [option.value, option.label]))

    return {
      avatar: { image: true, label: t('organisation:propertyManagers.form.avatar.label') },
      firstName: { label: t('organisation:propertyManagers.form.firstName.label') },
      lastName: { label: t('organisation:propertyManagers.form.lastName.label') },
      ...(isEdit
        ? {}
        : {
            email: { label: t('organisation:propertyManagers.form.email.label') },
            password: {
              format: (value) => (value ? PASSWORD_MASK : EMPTY_VALUE),
              label: t('organisation:propertyManagers.form.password.label'),
            },
          }),
      buildingIds: {
        items: (value) => (value as string[]).map((id) => buildingNames.get(id) ?? id),
        label: t('organisation:propertyManagers.form.buildings.label'),
      },
      jobTitle: { label: t('organisation:propertyManagers.form.jobTitle.label') },
      phone: { label: t('organisation:propertyManagers.form.phone.label') },
    }
  }, [t, isEdit, buildingOptions])

  const guard = useDirtyFormGuard(
    isDirty && !isSubmitting,
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    { inPopup: true },
  )

  const handleClose = () => {
    guard(onClose)
    return false
  }

  const onSubmit: SubmitHandler<PropertyManagerFormData> = async (data) => {
    const avatar = data.avatar?.[0]
    const phone = getPhoneDigits(data.phone ?? '')
    const firstName = data.firstName.trim()
    const lastName = data.lastName.trim()
    const buildingIds = data.buildingIds.map(Number)

    try {
      if (isEdit && propertyManager) {
        await updatePropertyManager({
          avatar,
          buildingIds,
          firstName,
          id: propertyManager.id,
          jobTitle: data.jobTitle?.trim() ?? '',
          lastName,
          phone: phone || (propertyManager.phone ? 'null' : undefined),
        })
        toastFactory.success(t('organisation:propertyManagers.form.toasts.updated'))
      } else {
        await createPropertyManager({
          avatar,
          buildingIds,
          email: data.email.trim(),
          firstName,
          jobTitle: data.jobTitle?.trim() || undefined,
          lastName,
          password: data.password,
          phone: phone || undefined,
        })
        toastFactory.success(t('organisation:propertyManagers.form.toasts.created'))
      }
      onClose()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const submit = form.handleSubmit(onSubmit)
  const submitDisabled = isEdit && !isDirty

  return (
    <Modal
      autoWidth
      title={
        isEdit
          ? t('organisation:propertyManagers.form.titleEdit')
          : t('organisation:propertyManagers.form.titleNew')
      }
      headerIcon={UserIcon}
      footer={
        <>
          <Button use="secondary" onClick={handleClose}>
            {isEdit ? t('common:actions.cancel') : t('common:actions.back')}
          </Button>
          <Button disabled={submitDisabled} loading={isSubmitting} use="primary" onClick={submit}>
            {isEdit ? t('common:actions.save') : t('common:actions.add')}
            <Icon icon={isEdit ? CheckIcon : PlusIcon} />
          </Button>
        </>
      }
      onBeforeClose={handleClose}
      onClose={onClose}
    >
      <PersonFormLayout submitDisabled={submitDisabled} form={form} onSubmit={submit}>
        <AvatarFormField
          existingImageUrl={existingAvatarUrl}
          label={t('organisation:propertyManagers.form.avatar.label')}
          uploadLabel={t('organisation:propertyManagers.form.avatar.uploadLabel')}
        />

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:propertyManagers.form.firstName.label')}
            maxLength={PROPERTY_MANAGER_NAME_MAX_LENGTH}
            name="firstName"
            placeholder={t('organisation:propertyManagers.form.firstName.placeholder')}
          />
          <TextInputFormField
            withReservedFooter
            required
            label={t('organisation:propertyManagers.form.lastName.label')}
            maxLength={PROPERTY_MANAGER_NAME_MAX_LENGTH}
            name="lastName"
            placeholder={t('organisation:propertyManagers.form.lastName.placeholder')}
          />
        </Grid>

        <TextInputFormField
          withReservedFooter
          required
          autoComplete="off"
          disabled={isEdit}
          label={t('organisation:propertyManagers.form.email.label')}
          name="email"
          placeholder={t('organisation:propertyManagers.form.email.placeholder')}
        />

        {!isEdit && (
          <PasswordInputFormField
            withReservedFooter
            required
            autoComplete="new-password"
            label={t('organisation:propertyManagers.form.password.label')}
            name="password"
            placeholder={t('organisation:propertyManagers.form.password.placeholder')}
          />
        )}

        <Grid align="start" columns="repeat(2, minmax(0, 1fr))" gap={8}>
          <PhoneNumberFormField
            withReservedFooter
            label={t('organisation:propertyManagers.form.phone.label')}
            name="phone"
            placeholder={t('organisation:propertyManagers.form.phone.placeholder')}
            size="medium"
          />
          <TextInputFormField
            withReservedFooter
            label={t('organisation:propertyManagers.form.jobTitle.label')}
            maxLength={PROPERTY_MANAGER_JOB_TITLE_MAX_LENGTH}
            name="jobTitle"
            placeholder={t('organisation:propertyManagers.form.jobTitle.placeholder')}
          />
        </Grid>

        <ChipSelectFormField<PropertyManagerFormData>
          required
          label={t('organisation:propertyManagers.form.buildings.label')}
          name="buildingIds"
          options={buildingOptions}
          placeholder={t('organisation:propertyManagers.form.buildings.placeholder')}
          size="medium"
        />
      </PersonFormLayout>
    </Modal>
  )
}
